import { Box, Grid, Typography } from '@atgfr8/atg-ui-tailwind';
import {
  FalveyInsuranceQuote,
  fetchLtlQuoteLineItems,
  LtlQuoteLineItemViewModel,
  LtlQuoteViewModel,
} from '@store/orders';
import { createMemo, createResource, Show } from 'solid-js';

import { CostDetailCard } from './components/CostDetailCard';
interface ViewCostDetailsPopoverProps {
  quote?: LtlQuoteViewModel;
  insuranceQuote?: Pick<FalveyInsuranceQuote, 'premium' | 'serviceFee'>;
  lineItems?: LtlQuoteLineItemViewModel[];
}

export const ViewCostDetailsPopover = (props: ViewCostDetailsPopoverProps) => {
  const [fetchedLineItems] = createResource(
    () => {
      const providedLineItems = props.lineItems;
      const quoteLineItems = props.quote?.lineItems;

      // When lineItems is explicitly provided by the caller (even if empty),
      // rely on caller-owned fetching to avoid duplicate requests.
      if (providedLineItems != null) {
        return null;
      }

      const hasQuoteLineItems = (quoteLineItems?.length ?? 0) > 0;
      const shouldFetchLineItems = !hasQuoteLineItems;

      return shouldFetchLineItems && props.quote?.id ? props.quote.id : null;
    },
    async (quoteId) => {
      try {
        return await fetchLtlQuoteLineItems(quoteId);
      } catch {
        // Already logged by fetchLtlQuoteLineItems; fall back to no line items so the popover still renders.
        return [] as LtlQuoteLineItemViewModel[];
      }
    },
  );

  const lineItems = createMemo<LtlQuoteLineItemViewModel[]>(() => {
    const providedLineItems = props.lineItems;
    if (providedLineItems != null) {
      return providedLineItems;
    }

    const quoteLineItems = props.quote?.lineItems;
    if (quoteLineItems && quoteLineItems.length > 0) {
      return quoteLineItems;
    }

    return fetchedLineItems() ?? [];
  });

  return (
    <Box class="max-w-[600px]">
      <Grid container spacing={2} class="p-4">
        <Grid item size={6}>
          <Typography variant="h6" class="pl-4">
            Carrier Cost
          </Typography>
          <Show when={props.quote}>
            {(quote) => (
              <CostDetailCard
                isCustomerCost={false}
                quote={quote()}
                insuranceQuote={props.insuranceQuote}
                lineItems={lineItems()}
              />
            )}
          </Show>
        </Grid>
        <Show when={(props.quote?.customerTotal ?? 0) > 0 && props.quote}>
          {(quote) => (
            <Grid item size={6}>
              <Typography variant="h6" class="pl-4">
                Customer Cost
              </Typography>
              <CostDetailCard
                isCustomerCost={true}
                insuranceQuote={props.insuranceQuote}
                quote={quote()}
                lineItems={lineItems()}
              />
            </Grid>
          )}
        </Show>
      </Grid>
    </Box>
  );
};
